import React from 'react';
import { Device, Position } from '../../types';
import { VehicleConfigOut } from '../../services/api';
import { parseAttributes } from '../../utils/parseAttributes';
import { parseUtc } from '../../utils/dataAge';
import './DeviceList.css';

interface DeviceListProps {
  devices: Device[];
  positions: Map<number, Position>;
  selectedDeviceId: number | null;
  onSelectDevice: (deviceId: number) => void;
  vehicleConfigs: Map<number, VehicleConfigOut>;
  query: string;
  onQueryChange: (q: string) => void;
}

const getCountryFlag = (lat: number, lng: number): string => {
  if (lat >= 49.0 && lat <= 54.9 && lng >= 14.1 && lng <= 24.2) return '🇵🇱';
  if (lat >= 47.3 && lat <= 55.1 && lng >= 5.9  && lng <= 15.0) return '🇩🇪';
  if (lat >= 45.8 && lat <= 51.1 && lng >= 12.4 && lng <= 22.9) return '🇨🇿';
  if (lat >= 47.7 && lat <= 49.6 && lng >= 16.8 && lng <= 22.6) return '🇸🇰';
  if (lat >= 45.7 && lat <= 48.6 && lng >= 16.1 && lng <= 23.0) return '🇭🇺';
  if (lat >= 50.8 && lat <= 58.1 && lng >= 21.0 && lng <= 32.1) return '🇺🇦';
  if (lat >= 41.2 && lat <= 44.2 && lng >= 22.3 && lng <= 28.7) return '🇧🇬';
  if (lat >= 43.6 && lat <= 48.3 && lng >= 22.1 && lng <= 30.1) return '🇷🇴';
  return '🏳️';
};

const DeviceList: React.FC<DeviceListProps> = ({ devices, positions, selectedDeviceId, onSelectDevice, vehicleConfigs, query, onQueryChange }) => {

  const filtered = query.trim() === '' ? devices : (() => {
    const q = query.trim().toLowerCase();
    return devices.filter(d =>
      d.name.toLowerCase().includes(q) ||
      (vehicleConfigs.get(d.id)?.registration_number ?? '').toLowerCase().includes(q) ||
      d.uniqueid.toLowerCase().includes(q)
    );
  })();

  const onlineCount = filtered.filter(d => d.status?.trim() === 'online').length;
  const totalOnline = devices.filter(d => d.status?.trim() === 'online').length;

  return (
    <div className="device-list">
      <div className="device-list-header">
        <span>Pojazdy ({query.trim() ? `${filtered.length}/${devices.length}` : devices.length})</span>
        <span>🟢 {query.trim() ? onlineCount : totalOnline} &nbsp;🔴 {query.trim() ? filtered.length - onlineCount : devices.length - totalOnline}</span>
      </div>
      <div className="device-search">
        <input
          type="text"
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          placeholder="Szukaj po nazwie, nr rej. lub ID urządzenia..."
          className="device-search-input"
        />
      </div>
      <div className="device-table-wrap">
        <table className="device-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Pojazd</th>
              <th title="Kraj">🌍</th>
              <th title="Prędkość">km/h</th>
              <th title="Stacyjka">🔑</th>
              <th title="e-TOLL">eTOLL</th>
              <th title="Przebieg całkowity">Przebieg</th>
              <th title="Ostatni kontakt">Ostatni kontakt</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((device, index) => {
              const position = positions.get(device.id);
              const attrs = parseAttributes(position?.attributes);
              const isOnline = device.status?.trim() === 'online';
              const isSelected = selectedDeviceId === device.id;
              const ignition = attrs.ignition;
              // speed_kmh obliczane przez backend; speed (knoty) deprecated.
              const speed = Math.round(position?.speed_kmh ?? 0);
              const isOnlineStrict = isOnline && !!device.lastupdate &&
                (Date.now() - parseUtc(device.lastupdate).getTime()) < 10 * 60_000;
              const cfg = vehicleConfigs.get(device.id);
              const isHorta = (cfg?.device_type ?? '').toLowerCase().includes('horta');
              // Horta: canOdometer z CP1 (km, 1 dec). Nie fallback na GPS (136k ≠ 206k).
              // Teltonika: io87 (metry → km). Fallback GPS tylko dla non-Horta.
              const canKm = isHorta
                ? (typeof attrs.canOdometer === 'number' ? attrs.canOdometer.toFixed(1) : null)
                : (typeof attrs.io87 === 'number' ? (attrs.io87 / 1000).toFixed(0) : null);
              const gpsKm = attrs.totalDistance ? ((attrs.totalDistance as number) / 1000).toFixed(0) : null;
              const odomKm = canKm ?? (isHorta ? null : gpsKm);
              const flag = position ? getCountryFlag(position.latitude, position.longitude) : '—';
              const lastUpdate = device.lastupdate
                ? parseUtc(device.lastupdate).toLocaleString('pl-PL', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  })
                : '—';

              return (
                <tr
                  key={device.id}
                  className={`device-row${isSelected ? ' selected' : ''}${!isOnline ? ' offline' : ''}`}
                  onClick={() => onSelectDevice(device.id)}
                >
                  <td className="c-lp">{index + 1}</td>
                  <td className="c-name">
                    <span className={`dot ${isOnline ? 'g' : 'r'}`} />
                    {device.name}
                  </td>
                  <td className="c-center">{flag}</td>
                  <td className={`c-center${!isOnlineStrict && position ? ' stale-speed' : ''}`}
                      title={!isOnlineStrict && position ? 'Ostatni pomiar — urządzenie offline' : ''}>
                    {position ? `${speed}` : '—'}
                  </td>
                  <td className="c-center">
                    <span className={ignition ? 'ign-on' : 'ign-off'}>⬤</span>
                  </td>
                  <td className="c-center">
                    <span title="Brak danych">⬜</span>
                  </td>
                  <td className="c-right" title={isHorta ? 'Przebieg (CAN)' : 'Przebieg GPS'}>
                    {odomKm ? `${odomKm} km` : '—'}
                  </td>
                  <td className="c-right">{lastUpdate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeviceList;
